// Shared student-specific assignment timing resolver.
//
// This is intentionally a pure utility: it does not fetch data itself.
// Callers provide assignments plus the relevant settings, enrolments,
// group memberships, group deadlines, and submission timing records.

export function addDaysToDateString(dateStr, days) {
  if (!dateStr || !days) return dateStr || null;

  const parts = String(dateStr)
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(Number.isFinite)
  ) {
    return dateStr;
  }

  const date = new Date(
    parts[0],
    parts[1] - 1,
    parts[2],
    12,
    0,
    0
  );

  date.setDate(
    date.getDate() + Number(days)
  );

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
}

export function applyStudentAssignmentTiming({
  assignments,
  assignmentTypeSettings,
  enrolments,
  memberships,
  deadlines,
  submissions
}) {
  const enrolmentIdByCourse = new Map(
    (enrolments || []).map(enrolment => [
      String(
        enrolment?.courses?.id ||
        enrolment?.course_id ||
        ''
      ),
      enrolment.id
    ])
  );

  const settingsByCourseAndType = new Map(
    (assignmentTypeSettings || []).map(row => [
      `${row.course_id}|${row.assignment_type_id}`,
      row
    ])
  );

  const membershipByAssignmentAndEnrolment = new Map(
    (memberships || []).map(row => [
      `${row.course_assignment_id}|${row.enrolment_id}`,
      row
    ])
  );

  const deadlineByAssignmentAndGroup = new Map(
    (deadlines || []).map(row => [
      `${row.course_assignment_id}|${row.group_number}`,
      row
    ])
  );

  /*
   * Extensions are stored on the parent/base
   * assignment submission row.
   *
   * Historically duplicate parent rows may exist.
   * Never allow a later 0-day row to erase a real
   * extension: retain the largest extension value.
   */
  const extensionByAssignmentAndEnrolment =
    new Map();

  (submissions || []).forEach(row => {
    if (
      row?.course_assignment_schedule_item_id ||
      row?.structured_form_instance_id
    ) {
      return;
    }

    const key =
      `${row.course_assignment_id}|${row.enrolment_id}`;

    const extensionDays =
      Math.max(
        0,
        Number(
          row?.extension_days
        ) || 0
      );

    const existing =
      extensionByAssignmentAndEnrolment
        .get(key) || 0;

    extensionByAssignmentAndEnrolment
      .set(
        key,
        Math.max(
          existing,
          extensionDays
        )
      );
  });

  return (assignments || []).map(assignment => {
    const enrolmentId = enrolmentIdByCourse.get(
      String(assignment.course_id)
    );

    const settings = settingsByCourseAndType.get(
      `${assignment.course_id}|${assignment.assignment_type_id}`
    );

    const effectiveSequenceId =
      settings?.sequence_id || null;

    const requiresSubmission =
      assignment.assignment_types?.requires_submission ?? true;

    if (!requiresSubmission) {
      return {
        ...assignment,
        effective_sequence_id: effectiveSequenceId,
        student_assignment_timing_resolved: true,
        student_assignment_applicable: true,
        student_assignment_timing_source: 'occurrence',
        effective_open_date: null,
        effective_base_due_date: null,
        effective_due_date: null,
        effective_extension_days: 0
      };
    }

    const usesGroupDeadlines =
      settings?.group_deadlines_enabled === true &&
      assignment.groups_enabled === true;

    let applicable = true;
    let timingSource = 'standard';
    let openDate = assignment.open_date || null;
    let baseDueDate = assignment.due_date || null;

    if (usesGroupDeadlines && enrolmentId) {
      timingSource = 'group';

      const membership =
        membershipByAssignmentAndEnrolment.get(
          `${assignment.id}|${enrolmentId}`
        );

      if (!membership) {
        applicable = false;
        openDate = null;
        baseDueDate = null;
      } else {
        const deadline = deadlineByAssignmentAndGroup.get(
          `${assignment.id}|${membership.group_number}`
        );

        openDate = deadline?.open_date || null;
        baseDueDate = deadline?.due_date || null;
      }
    }

    let extensionDays = 0;
    let dueDate = baseDueDate;

    if (applicable && baseDueDate && enrolmentId) {
      extensionDays =
        extensionByAssignmentAndEnrolment
          .get(
            `${assignment.id}|${enrolmentId}`
          ) || 0;

      if (extensionDays > 0) {
        dueDate = addDaysToDateString(
          baseDueDate,
          extensionDays
        );
      }
    }

    return {
      ...assignment,
      effective_sequence_id: effectiveSequenceId,
      student_assignment_timing_resolved: true,
      student_assignment_applicable: applicable,
      student_assignment_timing_source: timingSource,
      effective_open_date: openDate,
      effective_base_due_date: baseDueDate,
      effective_due_date: dueDate,
      effective_extension_days: extensionDays
    };
  });
}

export function getStudentAssignmentOpenDate(assignment) {
  if (assignment?.student_assignment_applicable === false) {
    return null;
  }

  if (assignment?.student_assignment_timing_resolved) {
    return assignment.effective_open_date || null;
  }

  return assignment?.open_date || null;
}

export function getStudentAssignmentDueDate(assignment) {
  if (assignment?.student_assignment_applicable === false) {
    return null;
  }

  if (assignment?.student_assignment_timing_resolved) {
    return assignment.effective_due_date || null;
  }

  return assignment?.due_date || null;
}

export function getStudentAssignmentBaseDueDate(assignment) {
  if (assignment?.student_assignment_applicable === false) {
    return null;
  }

  if (assignment?.student_assignment_timing_resolved) {
    return assignment.effective_base_due_date || null;
  }

  return assignment?.due_date || null;
}

export function getStudentAssignmentDate(assignment) {
  if (assignment?.student_assignment_applicable === false) {
    return null;
  }

  const requiresSubmission =
    assignment?.assignment_types?.requires_submission ?? true;

  if (!requiresSubmission) {
    return assignment?.occurs_on || null;
  }

  return getStudentAssignmentDueDate(assignment);
}
